import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Avatar, Button, Drawer, Dropdown, Badge } from "antd";
import { useAuthState } from "react-firebase-hooks/auth";
import { useUserData } from "../../hooks/useUsers";
import { auth } from "./../../firebaseConfig";
import { signOut } from "firebase/auth";
import logo from "../../assets/images/logo.png";
import { 
  MenuOutlined, 
  DownOutlined, 
  SearchOutlined,
  HeartOutlined,
  MessageOutlined,
  UserOutlined,
  LogoutOutlined,
  DeleteOutlined,
  SettingOutlined,
  PlusOutlined,
  EditOutlined
} from "@ant-design/icons";

function AppHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(false);
  const [user] = useAuthState(auth);
  const { userData } = useUserData(user?.uid);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  function signout() {
    signOut(auth);
    window.location = "/";
  }

  const showDrawer = () => {
    setVisible(true);
  };

  const onClose = () => {
    setVisible(false);
  };

  const dropdownItems = [
    !userData?.profileCompleted && {
      label: (
        <Link to="/onboarding" style={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}>
          <SettingOutlined style={{ marginRight: '12px', color: '#667eea' }} />
          Complete Profile
        </Link>
      ),
      key: "onboarding",
    },
    userData?.profileCompleted && {
      label: (
        <Link to="/edit-profile" style={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}>
          <EditOutlined style={{ marginRight: '12px', color: '#667eea' }} />
          Edit Profile
        </Link>
      ),
      key: "edit-profile",
    },
    userData?.profileCompleted && {
      label: (
        <Link to="/interests" style={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}>
          <HeartOutlined style={{ marginRight: '12px', color: '#f56565' }} />
          Interests
        </Link>
      ),
      key: "interests",
    },
    userData?.profileCompleted && {
      label: (
        <Link to="/messenger" style={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}>
          <MessageOutlined style={{ marginRight: '12px', color: '#48bb78' }} />
          Messages
        </Link>
      ),
      key: "messenger",
    },
    userData?.userRole === "ADMIN" && userData?.profileCompleted && {
      label: (
        <Link to="/add-blog" style={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}>
          <EditOutlined style={{ marginRight: '12px', color: '#ed8936' }} />
          Add Blog
        </Link>
      ),
      key: "add-blog",
    },
    {
      type: "divider",
    },
    {
      label: (
        <Link to="#0" onClick={signout} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', color: '#6b7280' }}>
          <LogoutOutlined style={{ marginRight: '12px' }} />
          Logout
        </Link>
      ),
      key: "logout",
    },
    {
      type: "divider",
    },
    // {
    //   label: (
    //     <Link to="/delete" style={{ display: 'flex', alignItems: 'center', padding: '8px 0', color: '#ef4444' }}>
    //       <DeleteOutlined style={{ marginRight: '12px' }} />
    //       Delete Account
    //     </Link>
    //   ),
    //   key: "delete",
    // },
  ].filter(Boolean);

  return (
    <header style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      background: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.98)',
      backdropFilter: 'blur(12px)',
      borderBottom: scrolled ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid transparent',
      transition: 'all 0.3s ease',
      boxShadow: scrolled ? '0 4px 20px rgba(0, 0, 0, 0.08)' : 'none'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '80px'
      }}>
        
        {/* Logo - Keep your original logo */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
            <img 
              src={logo} 
              alt="OkBrahmin Logo" 
              style={{
                height: scrolled ? '30px' : '35px',
                width: 'auto',
                transition: 'all 0.3s ease',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
              }}
            />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '32px',
          '@media (max-width: 768px)': {
            display: 'none'
          }
        }} className="mobileHidden">
          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
              
              {/* Search Button - Only if profile completed */}
              {userData?.profileCompleted && (
                <Link to="/search" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  borderRadius: '25px',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '14px',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                }}
                >
                  <SearchOutlined />
                  Find Matches
                </Link>
              )}

              {/* Quick Action Icons - Only if profile completed */}
              {userData?.profileCompleted && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <Link to="/interests" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    background: '#fef2f2',
                    color: '#f56565',
                    fontSize: '18px',
                    transition: 'all 0.3s ease',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f56565';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#fef2f2';
                    e.currentTarget.style.color = '#f56565';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  >
                    <Badge count={3} size="small">
                      <HeartOutlined />
                    </Badge>
                  </Link>

                  <Link to="/messenger" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    background: '#f0fff4',
                    color: '#48bb78',
                    fontSize: '18px',
                    transition: 'all 0.3s ease',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#48bb78';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f0fff4';
                    e.currentTarget.style.color = '#48bb78';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  >
                    <Badge count={5} size="small">
                      <MessageOutlined />
                    </Badge>
                  </Link>
                </div>
              )}

              {/* User Dropdown */}
              <Dropdown
                menu={{ items: dropdownItems }}
                trigger={["click"]}
                placement="bottomRight"
                overlayStyle={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '8px 16px',
                  borderRadius: '25px',
                  background: 'rgba(102, 126, 234, 0.1)',
                  border: '1px solid rgba(102, 126, 234, 0.2)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(102, 126, 234, 0.15)';
                  e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.2)';
                }}
                >
                  <Avatar 
                    src={user.photoURL} 
                    size={32}
                    style={{
                      border: '2px solid white',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <UserOutlined />
                  </Avatar>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start'
                  }}>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#1f2937',
                      lineHeight: '1'
                    }}>
                      {user.displayName?.split(' ')[0] || 'User'}
                    </span>
                    <span style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      lineHeight: '1'
                    }}>
                      {userData?.profileCompleted ? 'Active' : 'Setup Required'}
                    </span>
                  </div>
                  <DownOutlined style={{ fontSize: '12px', color: '#6b7280' }} />
                </div>
              </Dropdown>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Link to="/login">
                <Button
                  size="large"
                  style={{
                    borderRadius: '25px',
                    height: '44px',
                    padding: '0 24px',
                    fontWeight: '600',
                    border: '2px solid #667eea',
                    color: '#667eea',
                    background: 'transparent'
                  }}
                >
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button
                  type="primary"
                  size="large"
                  style={{
                    borderRadius: '25px',
                    height: '44px',
                    padding: '0 24px',
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    border: 'none',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                  }}
                >
                  Join Free
                </Button>
              </Link>
            </div>
          )}
        </nav>

        {/* Mobile Menu Button */}
        <div className="mobileVisible" style={{ display: 'none', '@media (max-width: 768px)': { display: 'block' } }}>
          <Button
            type="text"
            onClick={showDrawer}
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(102, 126, 234, 0.1)',
              color: '#667eea',
              fontSize: '18px'
            }}
          >
            <MenuOutlined />
          </Button>

          {/* Mobile Drawer */}
          <Drawer
            title={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <img 
                  src={logo} 
                  alt="OkBrahmin Logo" 
                  style={{
                    height: '30px',
                    width: 'auto'
                  }}
                />
              </div>
            }
            placement="right"
            onClose={onClose}
            open={visible}
            width={300}
            style={{
              borderTopLeftRadius: '20px',
              borderBottomLeftRadius: '20px'
            }}
          >
            {user ? (
              <div style={{ padding: '20px 0' }}>
                {/* User Info */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px',
                  background: 'rgba(102, 126, 234, 0.05)',
                  borderRadius: '12px',
                  marginBottom: '24px'
                }}>
                  <Avatar src={user.photoURL} size={48}>
                    <UserOutlined />
                  </Avatar>
                  <div>
                    <div style={{ fontWeight: '600', color: '#1f2937' }}>
                      {user.displayName}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {userData?.profileCompleted ? 'Profile Active' : 'Complete Setup'}
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {userData?.profileCompleted && userData?.userRole === "ADMIN" && (
                    <Link to="/search" onClick={onClose} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      borderRadius: '12px',
                      color: '#1f2937',
                      textDecoration: 'none',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                    >
                      <SearchOutlined style={{ color: '#667eea' }} />
                      Search
                    </Link>
                  )}

                  {dropdownItems.filter(item => item.type !== 'divider').map((item) => (
                    <div key={item.key} onClick={onClose} style={{
                      padding: '4px 0'
                    }}>
                      {item.label}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ padding: '20px 0', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Link to="/login" onClick={onClose}>
                  <Button
                    size="large"
                    style={{
                      width: '100%',
                      borderRadius: '12px',
                      height: '48px',
                      fontWeight: '600',
                      border: '2px solid #667eea',
                      color: '#667eea'
                    }}
                  >
                    Login
                  </Button>
                </Link>
                <Link to="/register" onClick={onClose}>
                  <Button
                    type="primary"
                    size="large"
                    style={{
                      width: '100%',
                      borderRadius: '12px',
                      height: '48px',
                      fontWeight: '600',
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      border: 'none'
                    }}
                  >
                    Join Free
                  </Button>
                </Link>
              </div>
            )}
          </Drawer>
        </div>
      </div>

      {/* CSS for responsive behavior */}
      <style jsx>{`
        @media (max-width: 768px) {
          .mobileHidden {
            display: none !important;
          }
          .mobileVisible {
            display: block !important;
          }
        }
        @media (min-width: 769px) {
          .mobileVisible {
            display: none !important;
          }
        }
      `}</style>
    </header>
  );
}

export default AppHeader;